// NOTAMs for the flight planner: the EFIN pre-flight information bulletins
// Fintraffic's AIS publishes (apps/api/src/weather/providers/aisFinland.ts
// parses them), and the pure rules that pick out what matters to a route.
// Shared so the browser filters against the member's own plan and the map
// with exactly the code the tests cover.
//
// Positions are GeoJSON order, [lon, lat], as in airspace.ts.

import type { Position } from './airspace'
import type { WeatherFreshness } from './weather'

/** The bulletin's own sections: AERODROMES, EN-ROUTE and NAV WARNINGS. */
export const NOTAM_SECTIONS = ['aerodrome', 'enroute', 'warning'] as const
export type NotamSection = (typeof NOTAM_SECTIONS)[number]

/** A point the text names (`PSN 643917.2N 0281357.4E`), with its radius if one follows. */
export interface NotamArea {
  position: Position
  radiusNm: number | null
}

export interface Notam {
  /** `A5456/26`. */
  id: string
  fir: string
  /**
   * Every section the bulletins list it under: an aerodrome's NOTAM can
   * also be en-route information, or a navigation warning.
   */
  sections: NotamSection[]
  /** The aerodrome's ICAO code when it is listed under one, else null. */
  location: string | null
  locationName: string | null
  /** Item E, line breaks kept. */
  text: string
  from: string
  /** null: PERM. */
  to: string | null
  toEstimated: boolean
  /** Item D, as printed: `MON-FRI 0500-1500`. */
  schedule: string | null
  /** Items F and G as printed: `SFC`, `2700FT AMSL`, `FL080`. */
  lower: string | null
  upper: string | null
  areas: NotamArea[]
  /** eAIP designators the text names (`EFD114A`, `EFR72`), to match airspace.geojson ids. */
  airspaces: string[]
}

export interface NotamBulletinSource {
  title: string
  url: string
  issuedAt: string | null
  /** As printed: `APRX 2000 UTC`. */
  nextUpdate: string | null
}

export interface NotamBulletin {
  provider: 'fintraffic'
  fir: 'EFIN'
  sources: (NotamBulletinSource & WeatherFreshness)[]
  notams: Notam[]
}

// --- Reading the text ----------------------------------------------------

const COORDINATE = /(\d{2})(\d{2})(\d{2}(?:\.\d+)?)?N\s*(\d{3})(\d{2})(\d{2}(?:\.\d+)?)?E/g
const RADIUS = /\bRADIUS\s*(?:OF\s*)?(\d+(?:\.\d+)?)\s*NM\b/
const DIAMETER = /\bDIAMETER\D{0,12}?(\d+(?:\.\d+)?)\s*NM\b/

/**
 * Every coordinate in the text, each with the radius (or half the
 * diameter) stated before the next coordinate.
 */
export function findNotamAreas(text: string): NotamArea[] {
  const matches = [...text.matchAll(COORDINATE)]
  return matches.map((m, i) => {
    const [, latD, latM, latS, lonD, lonM, lonS] = m
    const lat = Number(latD) + Number(latM) / 60 + Number(latS ?? 0) / 3600
    const lon = Number(lonD) + Number(lonM) / 60 + Number(lonS ?? 0) / 3600
    const start = (m.index ?? 0) + m[0].length
    const clause = text.slice(start, matches[i + 1]?.index ?? text.length)
    const radius = RADIUS.exec(clause)?.[1]
    const diameter = DIAMETER.exec(clause)?.[1]
    const radiusNm = radius ? Number(radius) : diameter ? Number(diameter) / 2 : null
    return { position: [lon, lat] as Position, radiusNm }
  })
}

// ENR 5.1's EFP/EFR/EFD areas and ENR 5.2's EFTRA/EFTSA. A digit must
// follow P/R/D, so an aerodrome code such as EFPO is not an area.
const AIRSPACE_REF = /\bEF(?:[PRD]\d{1,3}|TRA|TSA)[A-Z0-9]*\b/g

export function findAirspaceRefs(text: string): string[] {
  return [...new Set(text.match(AIRSPACE_REF) ?? [])]
}

/**
 * One NOTAM per id: the bulletins repeat a NOTAM under each section it
 * belongs to, and the two EFIN bulletins repeat the FIR's en-route
 * NOTAMs and warnings. First listing wins, sections are unioned.
 */
export function mergeNotams(notams: Notam[]): Notam[] {
  const byId = new Map<string, Notam>()
  for (const n of notams) {
    const seen = byId.get(n.id)
    if (!seen) {
      byId.set(n.id, { ...n, sections: [...n.sections] })
      continue
    }
    seen.sections = NOTAM_SECTIONS.filter((s) => seen.sections.includes(s) || n.sections.includes(s))
    if (!seen.location && n.location) {
      seen.location = n.location
      seen.locationName = n.locationName
    }
  }
  return [...byId.values()]
}

// --- Time ----------------------------------------------------------------

export type NotamStatus = 'active' | 'upcoming' | 'expired'

export function notamStatus(notam: Pick<Notam, 'from' | 'to'>, now: number): NotamStatus {
  if (Date.parse(notam.from) > now) return 'upcoming'
  if (notam.to !== null && Date.parse(notam.to) <= now) return 'expired'
  return 'active'
}

/** Whether the NOTAM's validity overlaps [from, to). The schedule is not interpreted. */
export function notamOverlaps(notam: Pick<Notam, 'from' | 'to'>, from: number, to: number): boolean {
  return Date.parse(notam.from) < to && (notam.to === null || Date.parse(notam.to) > from)
}

// --- Along a route -------------------------------------------------------

export interface RoutePoint {
  code: string
  lat: number
  lon: number
}

export type NotamReason = 'onRoute' | 'nearRoute' | 'airspace' | 'firWide'

export interface RouteNotam {
  notam: Notam
  reason: NotamReason
  /** Nearest approach to the route line, NM; null for a FIR-wide notice. */
  distanceNm: number | null
}

export interface RouteNotamOptions {
  /** Half-width of the corridor either side of the route line. */
  corridorNm?: number
  /** Aerodrome positions by ICAO code, for aerodrome NOTAMs off the route line. */
  aerodromes?: ReadonlyMap<string, { lat: number; lon: number }>
  /**
   * The outer ring of each of an area's polygons, by airspace.geojson id,
   * for warnings that name an area. Holes are ignored: a route through one
   * still counts, which errs toward showing the warning.
   */
  airspaces?: ReadonlyMap<string, Position[][]>
}

export const DEFAULT_CORRIDOR_NM = 10

type Xy = [number, number]

// A local equirectangular projection in NM: accurate to well under 1% over
// a club flight's few hundred miles, and cheap enough to run per NOTAM.
function projector(route: RoutePoint[]) {
  const lat0 = route.reduce((s, p) => s + p.lat, 0) / route.length
  const k = Math.cos((lat0 * Math.PI) / 180)
  return ([lon, lat]: Position): Xy => [lon * 60 * k, lat * 60]
}

function pointSegment(p: Xy, a: Xy, b: Xy): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2))
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy))
}

function segmentsCross(a: Xy, b: Xy, c: Xy, d: Xy): boolean {
  const orient = (p: Xy, q: Xy, r: Xy) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]))
  return orient(a, b, c) !== orient(a, b, d) && orient(c, d, a) !== orient(c, d, b)
}

function segmentSegment(a: Xy, b: Xy, c: Xy, d: Xy): number {
  if (segmentsCross(a, b, c, d)) return 0
  return Math.min(pointSegment(a, c, d), pointSegment(b, c, d), pointSegment(c, a, b), pointSegment(d, a, b))
}

function insideRing(p: Xy, ring: Xy[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i] as Xy
    const [xj, yj] = ring[j] as Xy
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** Segments of the route line; a single point is a zero-length segment. */
function routeSegments(line: Xy[]): [Xy, Xy][] {
  if (line.length === 1) return [[line[0] as Xy, line[0] as Xy]]
  return line.slice(1).map((b, i) => [line[i] as Xy, b])
}

function distanceToRoute(p: Xy, segments: [Xy, Xy][]): number {
  return Math.min(...segments.map(([a, b]) => pointSegment(p, a, b)))
}

function distanceToRing(ring: Xy[], segments: [Xy, Xy][]): number {
  if (ring.length < 3) return Infinity
  if (segments.some(([a]) => insideRing(a, ring))) return 0
  let best = Infinity
  for (let i = 1; i < ring.length; i++) {
    for (const [a, b] of segments) best = Math.min(best, segmentSegment(ring[i - 1] as Xy, ring[i] as Xy, a, b))
  }
  return best
}

const round1 = (n: number) => Math.round(n * 10) / 10

/**
 * The NOTAMs that matter to a flight along `route`:
 *
 * - one listed under an aerodrome on the plan, or under an aerodrome
 *   within the corridor (an alternate, or a field overflown);
 * - one with a position (or circle) reaching into the corridor, or naming
 *   an area whose polygon does;
 * - an en-route NOTAM or warning with no aerodrome, position or known
 *   area: it applies to the whole FIR.
 *
 * A warning that names only areas missing from `airspaces` (a temporary
 * area from an AIP SUP) is kept as FIR-wide rather than dropped: missing
 * geometry is not evidence that it is far away. Sorted by distance,
 * FIR-wide last.
 */
export function notamsAlongRoute(notams: Notam[], route: RoutePoint[], options: RouteNotamOptions = {}): RouteNotam[] {
  if (route.length === 0) return []
  const corridor = options.corridorNm ?? DEFAULT_CORRIDOR_NM
  const project = projector(route)
  const segments = routeSegments(route.map((p) => project([p.lon, p.lat])))
  const onPlan = new Set(route.map((p) => p.code.toUpperCase()))

  const result: RouteNotam[] = []
  for (const notam of notams) {
    let best: { reason: NotamReason; d: number } | null = null
    const consider = (reason: NotamReason, d: number) => {
      if (d <= corridor && (!best || d < best.d)) best = { reason, d }
    }

    if (notam.location) {
      if (onPlan.has(notam.location)) consider('onRoute', 0)
      const at = options.aerodromes?.get(notam.location)
      if (at) consider('nearRoute', distanceToRoute(project([at.lon, at.lat]), segments))
    }
    for (const area of notam.areas) {
      consider('nearRoute', Math.max(0, distanceToRoute(project(area.position), segments) - (area.radiusNm ?? 0)))
    }
    let knownAreas = 0
    for (const id of notam.airspaces) {
      const rings = options.airspaces?.get(id)
      if (!rings) continue
      knownAreas++
      for (const ring of rings) consider('airspace', distanceToRing(ring.map(project), segments))
    }

    const found = best as { reason: NotamReason; d: number } | null
    if (found) {
      result.push({ notam, reason: found.reason, distanceNm: round1(found.d) })
    } else if (!notam.location && notam.areas.length === 0 && knownAreas === 0) {
      result.push({ notam, reason: 'firWide', distanceNm: null })
    }
  }
  const rank = (r: RouteNotam) => r.distanceNm ?? Number.MAX_VALUE
  return result.sort((a, b) => rank(a) - rank(b))
}
